import React, { useMemo, useState } from 'react';
import { playerAvatarImgClassName } from '../../../utils/avatarClassNames';

function projectionYardsForRow(row, position) {
	if (position === 'QB') return row.proj_passing_yards || 0;
	if (position === 'RB') return row.proj_rushing_yards || 0;
	return row.proj_receiving_yards || 0;
}

function formatProjectionNumber(value) {
	const parsed = parseFloat(value);
	if (Number.isNaN(parsed)) return '0';
	return parsed.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export default function ProjectionsByPositionTable({ players, position }) {
	const defaultSortBy = position === 'QB'
		? 'proj_passing_yards'
		: position === 'RB'
		? 'proj_rushing_yards'
		: 'proj_receiving_yards';
	const [
		sortBy,
		setSortBy
	] = useState(defaultSortBy);
	const [
		sortDirection,
		setSortDirection
	] = useState('desc');

	const sorted = useMemo(
		() => {
			const items = [
				...players
			];

			const valueForSort = (player) => {
				if (sortBy === 'rank_default') return projectionYardsForRow(player, position);
				if (sortBy === 'name') return (player.name || '').toLowerCase();
				if (sortBy === 'team_name') return (player.team_name || '').toLowerCase();
				return parseFloat(player[sortBy]) || 0;
			};

			items.sort((a, b) => {
				const aValue = valueForSort(a);
				const bValue = valueForSort(b);

				if (typeof aValue === 'string' || typeof bValue === 'string') {
					const result = String(aValue).localeCompare(String(bValue));
					return sortDirection === 'asc' ? result : -result;
				}

				const result = aValue - bValue;
				return sortDirection === 'asc' ? result : -result;
			});

			return items;
		},
		[
			players,
			position,
			sortBy,
			sortDirection
		]
	);

	const handleSort = (column) => {
		if (sortBy === column) {
			setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortBy(column);
			setSortDirection('desc');
		}
	};

	const sortIndicator = (column) => {
		if (sortBy !== column) return '';
		return sortDirection === 'asc' ? ' ↑' : ' ↓';
	};

	const sortableHeaderClass = 'cursor-pointer hover:underline';

	return (
		<div className="overflow-x-auto">
			<table className="min-w-full text-sm text-gray-800 dark:text-dark-text">
				<thead>
					<tr className="border-b border-gray-200 dark:border-dark-border">
						<th className={`text-left py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('rank_default')}>Rank{sortIndicator('rank_default')}</th>
						<th className={`text-left py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('name')}>Player{sortIndicator('name')}</th>
						<th className={`text-left py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('team_name')}>Team{sortIndicator('team_name')}</th>
						{position === 'QB' ? (
							<>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_pass_attempts')}>Proj Pass Attempts{sortIndicator('proj_pass_attempts')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_passing_yards')}>Proj Pass Yds{sortIndicator('proj_passing_yards')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_passing_touchdowns')}>Proj Pass TD{sortIndicator('proj_passing_touchdowns')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_rush_attempts')}>Proj Rush Att{sortIndicator('proj_rush_attempts')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_rushing_yards')}>Proj Rush Yds{sortIndicator('proj_rushing_yards')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_rushing_touchdowns')}>Proj Rush TD{sortIndicator('proj_rushing_touchdowns')}</th>
							</>
						) : position === 'RB' ? (
							<>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_targets')}>Proj Tgt{sortIndicator('proj_targets')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_receptions')}>Proj Rec{sortIndicator('proj_receptions')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_receiving_yards')}>Proj Rec Yds{sortIndicator('proj_receiving_yards')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_receiving_touchdowns')}>Proj Rec TD{sortIndicator('proj_receiving_touchdowns')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_rush_attempts')}>Proj Rush Att{sortIndicator('proj_rush_attempts')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_rushing_yards')}>Proj Rush Yds{sortIndicator('proj_rushing_yards')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_rushing_touchdowns')}>Proj Rush TD{sortIndicator('proj_rushing_touchdowns')}</th>
							</>
						) : (
							<>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_targets')}>Proj Tgt{sortIndicator('proj_targets')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_receptions')}>Proj Rec{sortIndicator('proj_receptions')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_receiving_yards')}>Proj Rec Yds{sortIndicator('proj_receiving_yards')}</th>
								<th className={`text-right py-3 px-4 ${sortableHeaderClass}`} onClick={() => handleSort('proj_receiving_touchdowns')}>Proj Rec TD{sortIndicator('proj_receiving_touchdowns')}</th>
							</>
						)}
					</tr>
				</thead>
				<tbody>
					{sorted.map((player, index) => (
						<tr key={`${player.position}-${player.id}`} className="border-b border-gray-100 dark:border-dark-border">
							<td className="py-3 px-4">{index + 1}</td>
							<td className="py-3 px-4">{player.name}</td>
							<td className="py-3 px-4">
								<div className="flex items-center gap-2">
									<img
										className={`${playerAvatarImgClassName} w-6 h-6`}
										src={`${player.team_image_url}?w=50&h=50`}
										alt={player.team_name}
									/>
									{player.team_name}
								</div>
							</td>
							{position === 'QB' ? (
								<>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_pass_attempts)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_passing_yards)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_passing_touchdowns)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_rush_attempts)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_rushing_yards)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_rushing_touchdowns)}</td>
								</>
							) : position === 'RB' ? (
								<>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_targets)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_receptions)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_receiving_yards)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_receiving_touchdowns)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_rush_attempts)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_rushing_yards)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_rushing_touchdowns)}</td>
								</>
							) : (
								<>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_targets)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_receptions)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_receiving_yards)}</td>
									<td className="py-3 px-4 text-right">{formatProjectionNumber(player.proj_receiving_touchdowns)}</td>
								</>
							)}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
